// Content Service
// Handles all content-related API calls (blogs, patient stories, treatments)

import authService from './Auth.services.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://the-live-cure-be.vercel.app/api';

class ContentService {
  // Blog APIs
  async getAllBlogs(filters = {}) {
    try {
      const token = authService.getToken();
      const queryParams = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/blogs?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch blogs');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get all blogs error:', error);
      return { blogs: [] };
    }
  }

  async getBlogById(blogId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/blogs/${blogId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch blog');
      }

      const data = await response.json();
      return data.blog;
    } catch (error) {
      console.error('Get blog by ID error:', error);
      throw error;
    }
  }

  async getFeaturedBlogs() {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/blogs/featured`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch featured blogs');
      }

      const data = await response.json();
      return data.blogs;
    } catch (error) {
      console.error('Get featured blogs error:', error);
      return [];
    }
  }

  async searchBlogs(searchTerm) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/blogs/search?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to search blogs');
      }

      const data = await response.json();
      return data.blogs;
    } catch (error) {
      console.error('Search blogs error:', error);
      return [];
    }
  }

  // Patient Stories APIs
  async getAllPatientStories(filters = {}) {
    try {
      const token = authService.getToken();
      const queryParams = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/patient-stories?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patient stories');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get all patient stories error:', error);
      return { stories: [] };
    }
  }

  async getPatientStoryById(storyId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/patient-stories/${storyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patient story');
      }

      const data = await response.json();
      return data.story;
    } catch (error) {
      console.error('Get patient story by ID error:', error);
      throw error;
    }
  }

  async getFeaturedStories() {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/patient-stories/featured`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch featured stories');
      }

      const data = await response.json();
      return data.stories;
    } catch (error) {
      console.error('Get featured stories error:', error);
      return [];
    }
  }

  // Ophthalmology Treatment APIs
  async getOphthalmologyTreatment(diseaseName) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/treatments/ophthalmology/disease/${diseaseName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch treatment details');
      }

      const data = await response.json();
      return data.treatment;
    } catch (error) {
      console.error('Get ophthalmology treatment error:', error);
      throw error;
    }
  }

  async getAllOphthalmologyTreatments() {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/treatments/ophthalmology`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch treatments');
      }

      const data = await response.json();
      return data.treatments;
    } catch (error) {
      console.error('Get all ophthalmology treatments error:', error);
      return [];
    }
  }

  // Admin Content APIs
  async createBlog(blogData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/blogs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(blogData),
      });

      if (!response.ok) {
        throw new Error('Failed to create blog');
      }

      const data = await response.json();
      return data.blog;
    } catch (error) {
      console.error('Create blog error:', error);
      throw error;
    }
  }

  async updateBlog(blogId, blogData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/blogs/${blogId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(blogData),
      });

      if (!response.ok) {
        throw new Error('Failed to update blog');
      }

      const data = await response.json();
      return data.blog;
    } catch (error) {
      console.error('Update blog error:', error);
      throw error;
    }
  }

  async deleteBlog(blogId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/blogs/${blogId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete blog');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Delete blog error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const contentService = new ContentService();
export default contentService;
